import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    MaterialReactTable,
    useMaterialReactTable,
} from 'material-react-table';
import { Box, IconButton, Button, TextField } from '@mui/material';
import { AiOutlineExport } from "react-icons/ai";
import { BiMapPin } from "react-icons/bi";

// Csv export dependencies and configuration
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { mkConfig, generateCsv, download } from 'export-to-csv';

import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';

function DevicesDataTable({ data, location, setTargetMarker }) {

    dayjs.locale('en-gb');

    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    const [dateRange, setDateRange] = useState([null, null]);
    const [filteredData, setFilteredData] = useState([]);

    // Handle changes in the date range
    const handleDateChange = (newValue, index) => {
        setDateRange((prev) => {
            const updatedRange = [...prev];
            updatedRange[index] = newValue ? dayjs(newValue) : null;
            // console.log(updatedRange);
            return updatedRange;
        });
    };

    const clearDate = (newValue, index) => {
        setDateRange([]);
    };

    const filterByDateRange = (rowDate, startDate, endDate) => {
        const rowDayjs = dayjs(rowDate);
        // console.log('Start Date:', startDate);
        // console.log('End Date:', endDate);
        // console.log('Row Date:', rowDayjs);

        const startOfDay = startDate.startOf('day');
        const endOfDay = endDate.endOf('day');

        // console.log('Start of Day:', startOfDay);
        // console.log('End of Day:', endOfDay);

        return rowDayjs.isSameOrAfter(startOfDay, 'day') && rowDayjs.isSameOrBefore(endOfDay, 'day');
    };

    // Handle CSV export

    // Define the CSV configuration
    const csvConfig = mkConfig({
        fieldSeparator: ';',
        decimalSeparator: '.',
        useKeysAsHeaders: true,
        filename: `NMS_Devices_${location}_${new Date().toLocaleString()}`,
    });

    // Define the CSV export function
    const flattenRow = (row) => {
        return {
            name: row.name || 'N/A',
            deveui: row.deveui || 'N/A',
            status: row.status || 'N/A',
            locations: row.locations || 'N/A',
            rssiavg: row.rssiavg !== null && row.rssiavg !== undefined ? row.rssiavg : 'N/A',
            snravg: row.snravg !== null && row.snravg !== undefined ? row.snravg : 'N/A',
            lastuptimestamp: row.lastuptimestamp
                ? dayjs(row.lastuptimestamp).format('DD/MM/YYYY')
                : 'N/A',
        };
    };

    // Define the CSV rows export function
    const handleExportRows = (rows) => {
        const flattenedData = rows.map((row) => flattenRow(row.original));
        const csv = generateCsv(csvConfig)(flattenedData);
        download(csvConfig)(csv);
    };

    // Define the complete CSV export function
    const handleExportData = () => {
        const flattenedData = data.map((row) => flattenRow(row));
        const csv = generateCsv(csvConfig)(flattenedData);
        download(csvConfig)(csv);
    };

    const finalData = [];

    useEffect(() => {
        // console.log(dateRange);
    }, [dateRange]);

    useEffect(() => {
        // console.log(data);

        const filteredData = data.filter((row) => {
            const timestamp = row.lastuptimestamp;

            const [startDate, endDate] = dateRange;

            // Check if date filter is enabled (i.e., startDate or endDate are set)
            const isDateFilterEnabled = startDate || endDate;

            // If date filter is enabled and there's no timestamp, skip the row
            if (!isDateFilterEnabled && !timestamp) {
                // console.log("Row missing timestamp and date filter is enabled, excluding from results");
                return true; // Include rows without timestamp if date filter is not enabled
            }

            // If timestamp exists, proceed with normal processing
            const rowDate = dayjs(Number(timestamp)); // Convert the timestamp to a Dayjs object

            // Ensure rowDate is a valid Day.js object
            if (!rowDate.isValid()) {
                // console.error("Invalid row date:", rowDate);
                return false; // Skip invalid row dates
            }

            // console.log("Start Date:", startDate);
            // console.log("End Date:", endDate);
            // console.log("Row Date:", rowDate);

            // Convert startDate and endDate to Day.js objects
            const startDateObj = startDate ? dayjs(startDate) : null;
            const endDateObj = endDate ? dayjs(endDate) : null;

            // Check if rowDate is within the date range
            const isInRange =
                (!startDateObj || rowDate.isAfter(startDateObj, 'day') || rowDate.isSame(startDateObj, 'day')) &&
                (!endDateObj || rowDate.isBefore(endDateObj, 'day') || rowDate.isSame(endDateObj, 'day'));

            // Ensure timestamp is valid (not null or 0)
            return isInRange;
        });

        // Set the filtered data
        setFilteredData(filteredData);
        // console.log(filteredData);
        // console.log('filteredData.length: ' + filteredData.length);

        setSuccess(true);
    }, [data, dateRange]);

    // Count the number of devices per best LRR
    const bestlrrCounts = data.reduce((acc, item) => {
        const id = item.bestlrr;
        if (id !== null && id !== undefined) {
            acc[id] = (acc[id] || 0) + 1;
        }
        return acc;
    }, {});

    // Convert to an array, sort by count (descending), and format
    const filterSelectOptions = Object.entries(bestlrrCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([id, count]) => `${id} (${count})`);

    // Define columns
    const columns = useMemo(
        () => [
            {
                accessorKey: 'name',
                header: 'Name',
            },
            {
                accessorKey: 'deveui',
                header: 'DevEUI',
            },
            {
                accessorKey: 'status',
                header: 'Status',
                filterVariant: 'multi-select',
                filterSelectOptions: [...new Set(data.map(item => item.status))],
                filterFn: (row, _columnId, filterValue) => {
                    if (!filterValue.length) {
                        return true;
                    }
                    const status = row.getValue('status');
                    return filterValue.includes(status);
                },
            },
            {
                accessorKey: 'locations',
                header: 'Location',
                filterVariant: 'multi-select',
                filterSelectOptions: [...new Set(data.map(item => item.locations))],
                filterFn: (row, _columnId, filterValue) => {
                    if (!filterValue.length) {
                        return true;
                    }
                    const location = row.getValue('locations');
                    return filterValue.includes(location);
                },
            },
            {
                accessorKey: 'rssiavg',
                header: 'RSSI (Average)',
                Cell: ({ cell }) => (cell.getValue() ?? 'N/A'), // Display "N/A" if null
                filterVariant: 'range-slider',
                filterFn: (row, _columnId, filterValue) => {
                    const value = row.getValue('rssiavg');
                    if (value === null) return false; // Exclude null values in the range filter
                    const [min, max] = filterValue || [];
                    return value >= min && value <= max;
                },
                muiFilterSliderProps: {
                    marks: true,
                    max: -30,
                    min: -120,
                    step: 1,
                },
            },
            {
                accessorKey: 'snravg',
                header: 'SNR (Average)',
                Cell: ({ cell }) => (cell.getValue() ?? 'N/A'), // Display "N/A" if null
                filterVariant: 'range-slider',
                filterFn: (row, _columnId, filterValue) => {
                    const value = row.getValue('snravg');
                    if (value === null) return false; // Exclude null values in the range filter
                    const [min, max] = filterValue || [];
                    return value >= min && value <= max;
                },
                muiFilterSliderProps: {
                    marks: true,
                    max: 15,
                    min: -20,
                    step: 1,
                },
            },
            {
                accessorKey: 'espavg',
                header: 'ESP (Average)',
                Cell: ({ cell }) => (cell.getValue() ?? 'N/A'), // Display "N/A" if null
                filterVariant: 'range-slider',
                filterFn: (row, _columnId, filterValue) => {
                    const value = row.getValue('espavg');
                    if (value === null) return false; // Exclude null values in the range filter
                    const [min, max] = filterValue || [];
                    return value >= min && value <= max;
                },
                muiFilterSliderProps: {
                    marks: true,
                    max: -40,
                    min: -140,
                    step: 1,
                },
            },
            {
                accessorKey: 'bestlrr',
                header: 'Best LRR',
                filterVariant: 'multi-select',
                filterSelectOptions, // Updated options with counts
                filterFn: (row, _columnId, filterValue) => {
                    if (!filterValue.length) {
                        return true;
                    }
                    const bestlrr = row.getValue('bestlrr');
                    return filterValue.some(option => option.startsWith(bestlrr)); // Match only the ID part
                },
            },
            {
                accessorKey: 'tags',
                header: 'Tags',
                // filterVariant: 'multi-select',
                // filterSelectOptions, // Updated options with counts
                // filterFn: (row, _columnId, filterValue) => {
                //     if (!filterValue.length) {
                //         return true;
                //     }
                //     const bestlrr = row.getValue('bestlrr');
                //     return filterValue.some(option => option.startsWith(bestlrr)); // Match only the ID part
                // },
            },
            {
                accessorKey: 'batlevel',
                header: 'Baterry Level (%)',
                Cell: ({ cell }) => (cell.getValue() ?? 'N/A'),
                filterVariant: 'range-slider',
                filterFn: (row, _columnId, filterValue) => {
                    const value = row.getValue('batlevel');
                    if (value === null) return false; // Exclude null values in the range filter
                    const [min, max] = filterValue || [];
                    return value >= min && value <= max;
                },
                muiFilterSliderProps: {
                    marks: true,
                    max: 101,
                    min: -1,
                    step: 1,
                },
            },
            {
                accessorKey: 'lastuptimestamp',
                header: 'Last Timestamp',
                Cell: ({ cell }) => {
                    const timestamp = cell.getValue();
                    return timestamp
                        ? dayjs(Number(timestamp)).format('DD/MM/YYYY')
                        : 'N/A';
                },
                Filter: () => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <div className='flex flex-col lg:flex-row'>
                                <DatePicker
                                    label="Start Date"
                                    format="DD/MM/YYYY"
                                    value={dateRange[0] ? dayjs(dateRange[0]) : null}
                                    onChange={(newValue) => handleDateChange(newValue, 0)}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                                <DatePicker
                                    label="End Date"
                                    format="DD/MM/YYYY"
                                    value={dateRange[1] ? dayjs(dateRange[1]) : null}
                                    onChange={(newValue) => handleDateChange(newValue, 1)}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                                <button onClick={clearDate}>Clear</button>
                            </div>
                        </Box>
                    </LocalizationProvider>
                ),
                filterFn: (row, _columnId, filterValue) => {
                    const [startDate, endDate] = filterValue || [];
                    const rowDate = row.getValue('lastuptimestamp'); // The value to filter

                    // Only filter if both start and end dates are available
                    if (startDate && endDate) {
                        return filterByDateRange(rowDate, startDate, endDate);
                    }

                    // If no date range is set, return true to show all rows
                    return true;
                },
            },
        ],
        [data, dateRange]
    );

    // Initialize table with `useMaterialReactTable`
    const table = useMaterialReactTable({
        data: filteredData,
        columns,
        getRowId: (row) => row.deveui,
        enableFilters: true,
        enableRowActions: true,
        renderRowActions: ({ row }) => (
            <Box sx={{ display: 'flex', gap: '8px' }}>
                <IconButton
                    onClick={() =>
                        // navigate(`/devices/${row.getValue('deveui')}`, {
                        //     state: { server: row.original.server },
                        // })
                        window.open(`/devices/${row.getValue('deveui')}/${row.original.server}`, '_blank')
                    }
                >
                    <AiOutlineExport />
                </IconButton>
                {row.original.latitude && row.original.longitude && <IconButton
                    onClick={() =>
                        setTargetMarker({
                            latitude: row.original.latitude,
                            longitude: row.original.longitude,
                        })
                    }
                >
                    <BiMapPin />
                </IconButton>}
            </Box>
        ),
        initialState: {
            showColumnFilters: true,
        },
        enableRowSelection: true,
        muiTableBodyProps: {
            sx: {
                backgroundColor: '#f0f0f0', // Change this color as needed
                color: '#333', // Optional: change text color if needed
            },
        },
        muiTableBodyProps: {
            sx: {
                backgroundColor: '#f7f7f7', // Set row background color
            },
        },
    });

    return (
        <div>
            {success &&
                <>
                    <Box sx={{ display: 'flex', gap: '16px', padding: '8px', flexWrap: 'wrap' }}>
                        <Button onClick={handleExportData} startIcon={<FileDownloadIcon />}>
                            Export All Data
                        </Button>
                        <Button
                            disabled={table.getPrePaginationRowModel().rows.length === 0}
                            onClick={() => handleExportRows(table.getPrePaginationRowModel().rows)}
                            startIcon={<FileDownloadIcon />}
                        >
                            Export All Rows
                        </Button>
                        <Button
                            disabled={table.getRowModel().rows.length === 0}
                            onClick={() => handleExportRows(table.getRowModel().rows)}
                            startIcon={<FileDownloadIcon />}
                        >
                            Export Page Rows
                        </Button>
                        <Button
                            disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                            onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
                            startIcon={<FileDownloadIcon />}
                        >
                            Export Selected Rows
                        </Button>
                    </Box>
                    <MaterialReactTable table={table} />
                </>
            }
        </div>
    );
}

const DevicesDataTableWithLocalization = ({ data, location, setTargetMarker }) => {
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DevicesDataTable data={data} location={location} setTargetMarker={setTargetMarker} />
        </LocalizationProvider>
    )
};

export default DevicesDataTableWithLocalization;